import {
  Descriptions,
  Table,
  Button,
  Badge,
  Space,
  Typography,
  Progress,
  Spin,
  Modal,
  Input,
} from 'antd';
import { useState } from 'react';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  usePurchaseOrderById,
  useApprovePO,
  useCancelPO,
} from '@hooks/usePurchasing';
import type {
  PurchaseOrderLineDto,
  PurchaseOrderStatus,
} from '@/types/purchasing';
import { downloadPurchaseOrderPdf } from '@/api/exportApi';

const MD_STATUS_COLOR_MAP: Record<PurchaseOrderStatus, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Approved: 'warning',
  PartiallyReceived: 'orange',
  Received: 'success',
  Cancelled: 'error',
};

const MD_STATUS_LABEL_MAP: Record<PurchaseOrderStatus, string> = {
  Draft: 'Чернетка',
  Submitted: 'Подано',
  Approved: 'Затверджено',
  PartiallyReceived: 'Частково отримано',
  Received: 'Отримано',
  Cancelled: 'Скасовано',
};

interface PurchaseOrderDetailPageProps {
  orderId: string;
  onBack: () => void;
  onNavigateToReceipt?: () => void;
}

export default function PurchaseOrderDetailPage({
  orderId,
  onBack,
  onNavigateToReceipt,
}: PurchaseOrderDetailPageProps) {
  const [mdIsCancelModalOpen, setMdIsCancelModalOpen] = useState(false);
  const [mdCancelReason, setMdCancelReason] = useState('');

  const { data: mdOrder, isLoading: mdIsLoading } = usePurchaseOrderById(orderId);
  const mdApprovePO = useApprovePO();
  const mdCancelPO = useCancelPO();

  const mdHandleApprove = () => {
    mdApprovePO.mutate(orderId);
  };

  const mdHandleCancel = () => {
    mdCancelPO.mutate(
      { id: orderId, reason: mdCancelReason },
      {
        onSuccess: () => {
          setMdIsCancelModalOpen(false);
          setMdCancelReason('');
        },
      }
    );
  };

  const mdCanApprove = mdOrder?.status === 'Submitted';
  const mdCanCancel =
    mdOrder?.status === 'Draft' ||
    mdOrder?.status === 'Submitted' ||
    mdOrder?.status === 'Approved';
  const mdCanReceive =
    mdOrder?.status === 'Approved' || mdOrder?.status === 'PartiallyReceived';

  const mdLineColumns: ColumnsType<PurchaseOrderLineDto> = [
    {
      title: 'SKU',
      dataIndex: 'productSku',
      key: 'productSku',
      width: 120,
    },
    {
      title: 'Назва',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Замовлено',
      dataIndex: 'quantityOrdered',
      key: 'quantityOrdered',
      width: 110,
      align: 'right',
    },
    {
      title: 'Отримано',
      dataIndex: 'quantityReceived',
      key: 'quantityReceived',
      width: 110,
      align: 'right',
    },
    {
      title: 'Залишилось',
      dataIndex: 'quantityPending',
      key: 'quantityPending',
      width: 110,
      align: 'right',
    },
    {
      title: 'Прогрес',
      key: 'progress',
      width: 160,
      render: (_: unknown, mdRecord: PurchaseOrderLineDto) => {
        const mdPercent =
          mdRecord.quantityOrdered > 0
            ? Math.round((mdRecord.quantityReceived / mdRecord.quantityOrdered) * 100)
            : 0;
        return <Progress percent={mdPercent} size="small" />;
      },
    },
    {
      title: 'Ціна',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Сума',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 130,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
  ];

  if (mdIsLoading || !mdOrder) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Замовлення {mdOrder.number}
        </Typography.Title>
        <Badge
          status={
            MD_STATUS_COLOR_MAP[mdOrder.status] as
              | 'default'
              | 'processing'
              | 'success'
              | 'error'
              | 'warning'
          }
          text={MD_STATUS_LABEL_MAP[mdOrder.status]}
        />
      </Space>

      <Descriptions bordered column={3} size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Постачальник">{mdOrder.supplierName}</Descriptions.Item>
        <Descriptions.Item label="Дата замовлення">
          {dayjs(mdOrder.orderDate).format('DD.MM.YYYY')}
        </Descriptions.Item>
        <Descriptions.Item label="Очікувана доставка">
          {mdOrder.expectedDeliveryDate
            ? dayjs(mdOrder.expectedDeliveryDate).format('DD.MM.YYYY')
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Валюта">{mdOrder.currency}</Descriptions.Item>
        <Descriptions.Item label="Курс">{mdOrder.exchangeRate}</Descriptions.Item>
        <Descriptions.Item label="Сума">
          {mdOrder.totalAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}{' '}
          {mdOrder.currency}
        </Descriptions.Item>
        {mdOrder.supplierInvoiceNumber && (
          <Descriptions.Item label="Рахунок постачальника">
            {mdOrder.supplierInvoiceNumber}
          </Descriptions.Item>
        )}
        {mdOrder.notes && (
          <Descriptions.Item label="Примітки" span={3}>
            {mdOrder.notes}
          </Descriptions.Item>
        )}
        {mdOrder.cancelReason && (
          <Descriptions.Item label="Причина скасування" span={3}>
            <Typography.Text type="danger">{mdOrder.cancelReason}</Typography.Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5}>Рядки замовлення</Typography.Title>

      <Table<PurchaseOrderLineDto>
        columns={mdLineColumns}
        dataSource={mdOrder.lines}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        <Button
          icon={<PrinterOutlined />}
          onClick={() => downloadPurchaseOrderPdf(orderId)}
        >
          Друк накладної
        </Button>
        {mdCanApprove && (
          <Button
            type="primary"
            onClick={mdHandleApprove}
            loading={mdApprovePO.isPending}
          >
            Затвердити
          </Button>
        )}
        {mdCanReceive && (
          <Button type="primary" onClick={onNavigateToReceipt}>
            Оприбуткувати
          </Button>
        )}
        {mdCanCancel && (
          <Button danger onClick={() => setMdIsCancelModalOpen(true)}>
            Відмінити
          </Button>
        )}
      </div>

      <Modal
        title="Скасування замовлення"
        open={mdIsCancelModalOpen}
        onOk={mdHandleCancel}
        onCancel={() => setMdIsCancelModalOpen(false)}
        confirmLoading={mdCancelPO.isPending}
        okText="Скасувати замовлення"
        okButtonProps={{ danger: true }}
        cancelText="Назад"
      >
        <Typography.Paragraph>
          Вкажіть причину скасування замовлення:
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          value={mdCancelReason}
          onChange={(mdEvent) => setMdCancelReason(mdEvent.target.value)}
          placeholder="Причина скасування..."
        />
      </Modal>
    </>
  );
}
